import { React, useEffect, useState } from "react";
import {
  IndexTable,
  Layout,
  LegacyCard,
  EmptySearchResult,
  Banner,
} from "@shopify/polaris";
import { eventApi } from "../../../redux/troubleshooting/events/Action";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { HistoricalSkeletonTable } from "../../../components/skeletonLoader";
import {
  displayDateTime,
  displayText,
  ReadOnlyTablePagination,
  SUPPORT_TABLE_PAGE_SIZE,
} from "./readOnlyTable";

const EventsTab = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const routeShopId = String(id ?? "");
  const [pagination, setPagination] = useState({
    shopId: routeShopId,
    pageNumber: 0,
  });
  const pageNumber =
    pagination.shopId === routeShopId ? pagination.pageNumber : 0;
  const dispatch = useDispatch();
  const eventTableData = useSelector((store) => store.requriedEventsData);
  const isCurrentStore = eventTableData.shopId === String(id);
  const event =
    isCurrentStore &&
    eventTableData.isSuccess &&
    Array.isArray(eventTableData.data?.content)
      ? eventTableData.data.content
      : [];
  const readError =
    isCurrentStore && eventTableData.isError ? eventTableData.error : null;
  const isLoading = !isCurrentStore || eventTableData.isRequest;

  useEffect(() => {
    const payload = {
      storeId: id,
      pageNumber,
      pageSize: SUPPORT_TABLE_PAGE_SIZE,
    };
    dispatch(eventApi(payload));
  }, [dispatch, id, pageNumber]);

  useEffect(() => {
    if (
      isCurrentStore &&
      (eventTableData?.data?.message === "Actual error message" ||
        eventTableData.error === "Actual error message")
    ) {
      navigate("/logout");
    }
  }, [eventTableData.data, eventTableData.error, isCurrentStore, navigate]);

  const eventType = (
    <span className="d-flex gap-10">
      <span>Event type</span>
    </span>
  );
  const occurredAt = (
    <span className="d-flex gap-10">
      <span>Occurred at</span>
    </span>
  );
  const externalId = (
    <span className="d-flex gap-10">
      <span>External ID</span>
    </span>
  );

  const emptyStateMarkup = (
    <EmptySearchResult
      title="No events found"
      description="No store events were returned for this page."
      withIllustration
    />
  );

  const resourceName = {
    singular: "Event",
    plural: "Events",
  };

  const rowMarkup = event?.map(
    (
      { id: eventId, eventType, occurredAt, externalId, reason, description },
      index
    ) => (
      <IndexTable.Row
        id={String(eventId ?? `${routeShopId}-event-${index}`)}
        key={String(eventId ?? `${routeShopId}-event-${index}`)}
        position={index}
      >
        <IndexTable.Cell>{displayDateTime(occurredAt)}</IndexTable.Cell>
        <IndexTable.Cell>{displayText(eventType)}</IndexTable.Cell>
        <IndexTable.Cell>{displayText(externalId)}</IndexTable.Cell>
        <IndexTable.Cell>{displayText(reason, "Not recorded")}</IndexTable.Cell>
        <IndexTable.Cell>
          {displayText(description, "Not recorded")}
        </IndexTable.Cell>
      </IndexTable.Row>
    )
  );

  const handlePageChange = (nextPage) =>
    setPagination({ shopId: routeShopId, pageNumber: nextPage });

  return (
    <>
      <Layout.Section>
        <div className="wrapper">
          <LegacyCard sectioned>
            <div className="d-flex flex-col" style={{ padding: "15px" }}>
              <p className="card_title">Events</p>
            </div>

            <LegacyCard>
              {isLoading ? (
                <HistoricalSkeletonTable />
              ) : readError ? (
                <Banner title="Events could not be loaded" status="critical">
                  <p>{readError}</p>
                </Banner>
              ) : (
                <IndexTable
                  resourceName={resourceName || 0}
                  itemCount={event?.length}
                  emptyState={emptyStateMarkup}
                  headings={[
                    { id: "occurred-at", title: occurredAt },
                    { id: "event-type", title: eventType },
                    { id: "external-id", title: externalId },
                    { id: "reason", title: "Reason" },
                    { id: "description", title: "Description" },
                  ]}
                  selectable={false}
                >
                  {rowMarkup}
                </IndexTable>
              )}
              {!isLoading && !readError ? (
                <ReadOnlyTablePagination
                  response={eventTableData.data}
                  currentPage={pageNumber}
                  rowCount={event.length}
                  noun="event"
                  onPageChange={handlePageChange}
                />
              ) : null}
            </LegacyCard>
          </LegacyCard>
        </div>
      </Layout.Section>
    </>
  );
};

export default EventsTab;
